import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import API from "../../Common/API"
import DatatableLoader from "../../Components/Loaders/DataTableLoader"
import { datatableTheme } from "../../Common/dataTableTheme"
import DataTable, { createTheme } from "react-data-table-component"
import InlineTextField from "../../Components/InLineTextField"
import { useDispatch } from "react-redux"
import * as Yup from "yup"
import { useFormik } from "formik"
import { setAlert } from "../../Store/Theme/actions"
import FieldInput from "../../Components/FieldInput"
import Dialog from "../../Components/Dialog"
import FieldSelect from "../../Components/FieldSelect"
import Button from "../../Components/Button"
import { FiEdit3, FiRefreshCw } from "react-icons/fi"
import TableActionButton from "../../Components/TableActionButton"
import { useHistory, useLocation } from "react-router-dom"
import UserRowActionsMenu from "../../Components/UserRowActionsMenu"

createTheme("defaultLight", datatableTheme.default)
createTheme("defaultDark", datatableTheme.dark)

const planDate = {
    planValidityDate: "",
}

const startOfToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
}

const PlanValidity = () => {
    const [planList, setPlanList] = useState([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState("")
    const dispatch = useDispatch()
    const [count, setCount] = useState(0)
    const [planEditDialog, setPlanEditDialog] = useState(false)
    const [formData, setFormData] = useState(planDate)
    const [currentPlan, setCurrentPlan] = useState()
    const [planMenu, setPlanMenu] = useState([])
    const [openchangeStatus, setOpenChangeStatus] = useState(false)
    const location = useLocation()
    const history = useHistory()

    useEffect(() => {
        setLoading(true)
        Promise.allSettled([
            API.get("/plans/getAllUserPlan"),
            API.get("/plans/getPlansList"),
        ]).then(([subscriptionsRes, plansRes]) => {
            if (subscriptionsRes.status === "fulfilled" && subscriptionsRes.value.status === 200) {
                setPlanList(subscriptionsRes.value.data.data)
            }
            if (plansRes.status === "fulfilled" && plansRes.value.status === 200) {
                setPlanMenu(plansRes.value.data.data.map((c) => ({ text: c.planName, value: c._id, validity: c.validityInDays || c.planValidity })))
            }
            setLoading(false)
        }).catch(() => setLoading(false))
    }, [count])

    const columns = useMemo(() => [
        { name: "S.No", selector: (row, i) => i + 1, initialActive: true, sortable: true, width: "70px" },
        { name: "Ref ID", selector: (row) => row.userId?.refId || "--", initialActive: true, sortable: true, width: "100px" },
        {
            name: "Member",
            minWidth: "180px",
            selector: (row) => `${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`.trim(),
            cell: (row) => (
                <div className="flex flex-col py-1">
                    <span className="font-semibold text-tColor">{`${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`.trim() || "--"}</span>
                    <span className="text-xs text-tpColor">{row.userId?.contactInformation?.mobileNumber || row.userId?.mobileNumber || ""}</span>
                </div>
            ),
        },
        { name: "Plan", selector: (row) => row.planId?.planName || "--", initialActive: true, sortable: true },
        { name: "Rate (₹)", selector: (row) => row.planId?.planRate || "--", sortable: true, width: "100px" },
        {
            name: "Renewal Date",
            width: "140px",
            selector: (row) => row.planValidityDate || "",
            cell: (row) => {
                const d = row.planValidityDate ? new Date(row.planValidityDate) : null
                const isExpired = d && d < new Date()
                const isToday = d && d.toDateString() === new Date().toDateString()
                return (
                    <div className="flex flex-col py-1">
                        <span className={`font-semibold ${isExpired ? "text-red-600" : isToday ? "text-amber-600" : "text-tColor"}`}>
                            {d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "--"}
                        </span>
                        {isToday && <span className="text-xs font-bold text-amber-600">Expires today</span>}
                        {isExpired && <span className="text-xs font-bold text-red-600">Expired</span>}
                    </div>
                )
            },
            sortable: true,
        },
        {
            name: "Status",
            width: "110px",
            selector: (row) => row.planValidityDate || "",
            cell: (row) => {
                const expired = row.planValidityDate && new Date(row.planValidityDate) < new Date()
                return <span className={`status-pill ${expired ? "status-pill--danger" : "status-pill--success"}`}>{expired ? "Expired" : "Active"}</span>
            },
        },
        {
            name: "Actions",
            width: "90px",
            cell: (row) => {
                const isExpired = row.planValidityDate && new Date(row.planValidityDate) < new Date()
                return (
                    <UserRowActionsMenu
                        actions={[
                            { label: "Edit Subscription", icon: FiEdit3, onClick: () => editPlans(row) },
                            isExpired && { label: "Renew Plan", icon: FiRefreshCw, onClick: () => openStatuses(row) },
                        ].filter(Boolean)}
                    />
                )
            },
        },
    ], [currentPlan, planMenu]) // eslint-disable-line react-hooks/exhaustive-deps

    const openStatuses = (row) => {
        setOpenChangeStatus(true)
        planForm.setValues({ ...planForm.values, userId: row.userId._id, planId: row.planId._id })
    }

    const flattenObjectValues = (obj) => {
        let result = []

        const recurse = (value) => {
            if (value === null || value === undefined) return
            if (typeof value === "object") {
                for (const key in value) {
                    recurse(value[key])
                }
            } else {
                result.push(value.toString().toLowerCase())
            }
        }

        recurse(obj)
        return result.join(" ")
    }

    const filteredcasedetails = useMemo(() => {
        const params = new URLSearchParams(location.search)
        const expiryFilter = params.get("expiry")
        const planId = params.get("planId")
        const today = startOfToday()
        const endOfWeek = new Date(today)
        endOfWeek.setDate(today.getDate() + 7)
        const filterSplit = filter.toLowerCase().split(" ")

        return planList.filter((c) => {
            const flatString = flattenObjectValues(c)
            const matchesSearch = filterSplit.every((term) => flatString.includes(term))
            const matchesPlan = planId ? c?.planId?._id === planId : true

            const expiryDate = new Date(c.planValidityDate)
            expiryDate.setHours(0, 0, 0, 0)

            const matchesExpiry = expiryFilter === "today"
                ? expiryDate.getTime() === today.getTime()
                : expiryFilter === "week"
                    ? expiryDate > today && expiryDate <= endOfWeek
                    : true

            return matchesSearch && matchesPlan && matchesExpiry
        })
    }, [filter, location.search, planList])

    const activeExpiryFilter = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return params.get("expiry")
    }, [location.search])

    const SubHeader = useMemo(() => (
        <div className="flex w-full flex-col gap-2">
            {activeExpiryFilter && (
                <div className={`flex items-center justify-between rounded-xl px-4 py-2 text-sm font-semibold ${activeExpiryFilter === "today" ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                    <span>
                        {activeExpiryFilter === "today" ? "🔴 Showing plans expiring TODAY" : "🟡 Showing plans expiring THIS WEEK"}
                        {" "}— {filteredcasedetails.length} member{filteredcasedetails.length !== 1 ? "s" : ""}
                    </span>
                    <button
                        type="button"
                        className="ml-4 rounded-full border px-3 py-1 text-xs font-bold transition hover:bg-white"
                        onClick={() => history.push("/planValidity")}
                    >
                        Clear filter
                    </button>
                </div>
            )}
            <div className="table-toolbar">
                <div className="toolbar-actions">
                    <span className="status-pill status-pill--warning">{filteredcasedetails.length} subscriptions</span>
                </div>
                <div className="toolbar-actions">
                    <InlineTextField label={"Subscriptions"} placeholder={"Search user, ref id, plan, or mobile"} value={filter} setValue={setFilter} />
                </div>
            </div>
        </div>
    ), [filter, filteredcasedetails.length, activeExpiryFilter, history])

    const editPlans = (value) => {
        setCurrentPlan(value)
        const parsedDate = value.planValidityDate ? new Date(value.planValidityDate) : new Date()
        // Normalize userId and planId to plain strings so formik values are never nested objects
        setFormData({
            userId: value.userId?._id || value.userId,
            planId: value.planId?._id || value.planId,
            planValidityDate: parsedDate,
        })
        setPlanEditDialog(true)
    }

    const planValiditySchema = Yup.object().shape({
        planId: Yup.string().required("Plan is required"),
        planValidityDate: Yup.string().required("Plan Validity Date is required")
    })

    const planForm = useFormik({
        initialValues: formData,
        enableReinitialize: true,
        validateOnMount: true,
        validationSchema: planValiditySchema,
        onSubmit: (values) => {
            const val = {
                // userId and planId are normalised to strings in editPlans(); guard both forms
                userId: values.userId?._id || values.userId,
                planId: values.planId?._id || values.planId,
                planValidityDate: new Date(values.planValidityDate).toISOString().split("T")[0]
            }
            API.post("/plans/editSubscription", val).then((planRes) => {
                if (planRes.status === 200) {
                    dispatch(setAlert({ title: "Success", subtitle: "Plan Edited Successfully", active: true, type: "success" }))
                    planForm.resetForm()
                    setPlanEditDialog(false)
                    setCount((prev) => prev + 1)
                }
            }).catch(() => {
                dispatch(setAlert({ title: "Error", subtitle: "Something went wrong", active: true, type: "error" }))
            })
        }
    })

    const changeValidatiyDate = (value) => {
        const updatedPlan = planMenu.find((plan) => plan.value === value)
        if (!updatedPlan) return
        // Base the new expiry on today (not the original createdAt) so the full validity is granted
        const planValidityDate = new Date()
        planValidityDate.setDate(planValidityDate.getDate() + parseInt(updatedPlan.validity))
        planForm.setFieldValue("planValidityDate", planValidityDate.toISOString().split("T")[0])
    }

    const activatePlan = () => {
        const selectedPlan = planMenu.find((x) => x.value === planForm.values.planId)
        if (!selectedPlan) {
            dispatch(setAlert({ title: "Error", subtitle: "Please select a plan first", active: true, type: "error" }))
            return
        }
        const validityDays = Number(selectedPlan.validity)
        if (!validityDays || isNaN(validityDays)) {
            dispatch(setAlert({ title: "Error", subtitle: "Selected plan has no valid validity period", active: true, type: "error" }))
            return
        }
        const apiValues = {
            planId: planForm.values.planId,
            userId: planForm.values.userId,
            planValidityDate: new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toISOString(),
            downloadCount: 0,
        }
        API.post("/plans/subscribe", apiValues).then((subRes) => {
            if (subRes.status === 200) {
                dispatch(setAlert({ title: "Success", subtitle: "Plan Activated Successfully", active: true, type: "success" }))
                setCount((prev) => prev + 1)
                setOpenChangeStatus(false)
            }
        }).catch(() => {
            dispatch(setAlert({ title: "Error", subtitle: "Something went wrong", active: true, type: "error" }))
        })
    }

    return (
        <motion.div className="space-y-5">
            {loading ? (
                <DatatableLoader />
            ) : (
                <motion.div className="datatable-shell panel-card h-full w-full overflow-hidden">
                    <DataTable
                        title="Subscription List"
                        theme="defaultLight"
                        columns={columns}
                        data={filteredcasedetails}
                        pagination
                        highlightOnHover
                        selectableRowsHighlight
                        persistTableHead
                        subHeader
                        subHeaderComponent={SubHeader}
                        noDataComponent={"There are no records to display"}
                    />
                </motion.div>
            )}
            <Dialog injectClass={"w-[450px] h-fit"} showDialog={planEditDialog} title={"Edit Plan Validity"} onClose={() => setPlanEditDialog(false)}>
                <motion.div className="flex w-full gap-5 my-2 flex-col">
                    <motion.div className="grid grid-cols-1 gap-5 my-2 w-full">
                        <p>Current Plan : {currentPlan?.planId?.planName}</p>
                        <FieldSelect label={"Edit Plan"} outerInjectClass={"w-full"} fieldHelper={planForm.getFieldHelpers("planId")} fieldMeta={planForm.getFieldMeta("planId")} fieldProps={planForm.getFieldProps("planId")} edit={true} dataLoaded={true} options={planMenu} onChange={(ev) => changeValidatiyDate(ev)} />
                        <FieldInput label={"Plan Validity Date"} outerInjectClass={"w-full"} type="date" fieldHelper={planForm.getFieldHelpers("planValidityDate")} fieldMeta={planForm.getFieldMeta("planValidityDate")} fieldProps={planForm.getFieldProps("planValidityDate")} edit={true} dataLoaded={true} />
                    </motion.div>
                    <motion.div className="flex flex-row justify-between gap-3">
                        <Button type="secondary" injectClass="rounded-full px-5" onClick={() => setPlanEditDialog(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" injectClass="rounded-full px-5" onClick={() => planForm.submitForm()}>
                            Save
                        </Button>
                    </motion.div>
                </motion.div>
            </Dialog>

            <Dialog injectClass="w-350px" showDialog={openchangeStatus} title={"Activate Plan"} onClose={() => setOpenChangeStatus(false)}>
                <motion.div className="w-full flex flex-col">
                    <FieldSelect label={"Choose Plan"} fieldProps={planForm.getFieldProps("planId")} fieldHelper={planForm.getFieldHelpers("planId")} fieldMeta={planForm.getFieldMeta("planId")} edit={true} dataLoaded={true} options={planMenu} />
                    <motion.div className="w-full flex flex-row justify-between mt-3">
                        <Button type="secondary" injectClass="rounded-full" onClick={() => setOpenChangeStatus(false)}>Cancel</Button>
                        <Button type="primary" injectClass="rounded-full" onClick={() => activatePlan()}>Activate</Button>
                    </motion.div>
                </motion.div>
            </Dialog>
        </motion.div>
    )
}

export default PlanValidity
